import { browser } from '$app/environment';

/**
 * Meta Pixel configuration from survey YAML
 */
export interface MetaPixelConfig {
  enabled: boolean;
  pixelId: string;
  events?: {
    landOnWelcome?: string;
    startFirstQuestion?: string;
    reachMidpoint?: string;
    completeSurvey?: string;
  };
  options?: {
    testMode?: boolean;
    midpointScreen?: number | null;
    customData?: Record<string, any>;
  };
}

/**
 * Service to manage Meta Pixel tracking
 * Handles browser-side event tracking for Meta ad optimization
 */
export class MetaPixelService {
  private config: MetaPixelConfig | null = null;
  private initialized = false;
  private surveyId: string | null = null;
  private totalScreens = 0;

  /**
   * Initialize the Meta Pixel service with survey configuration
   */
  initialize(surveyConfig: any): boolean {
    if (!browser) return false;

    const metaConfig = surveyConfig?.metaPixel;
    if (!metaConfig?.enabled || !metaConfig?.pixelId) {
      return false;
    }

    this.config = metaConfig;
    this.surveyId = surveyConfig?.id || null;
    this.initialized = true;

    // Set default event names if not configured
    if (!this.config.events) {
      this.config.events = {};
    }
    this.config.events.landOnWelcome = this.config.events.landOnWelcome || 'ViewContent';
    this.config.events.startFirstQuestion = this.config.events.startFirstQuestion || 'SurveyStarted';
    this.config.events.reachMidpoint = this.config.events.reachMidpoint || 'SurveyMidpoint';
    this.config.events.completeSurvey = this.config.events.completeSurvey || 'Lead';

    return true;
  }

  /**
   * Check if the pixel service is enabled and initialized
   */
  isEnabled(): boolean {
    return this.initialized && this.config?.enabled === true;
  }

  /**
   * Get the pixel ID
   */
  getPixelId(): string | null {
    return this.config?.pixelId || null;
  }

  /**
   * Check if test mode is enabled
   */
  isTestMode(): boolean {
    return this.config?.options?.testMode === true;
  }

  /**
   * Track page view (called automatically by pixel)
   */
  trackPageView(): void {
    if (!this.isEnabled() || !browser) return;

    const fbq = (window as any).fbq;
    if (typeof fbq === 'function') {
      fbq('track', 'PageView');
    }
  }

  /**
   * Handle screen progression tracking
   * Orchestrates all progression-related events based on screen transitions
   *
   * @param currentIndex - Current screen index
   * @param prevIndex - Previous screen index
   * @param surveyId - Survey identifier
   * @param totalScreens - Total number of screens
   */
  handleScreenProgression(
    currentIndex: number,
    prevIndex: number,
    surveyId: string,
    totalScreens: number
  ): void {
    if (!this.isEnabled() || !browser) return;

    // Store survey context
    this.surveyId = surveyId;
    this.totalScreens = totalScreens;

    // Track general screen view
    this.trackScreenView(currentIndex, totalScreens);

    // Track survey start (screen 0 → 1 transition)
    if (currentIndex === 1 && prevIndex === 0) {
      this.trackSurveyStart(surveyId);
    }

    // Track midpoint (only on forward progression)
    if (currentIndex > prevIndex) {
      this.trackSurveyMidpoint(surveyId, currentIndex);
    }
  }

  /**
   * Track screen view and automatically fire appropriate events
   */
  trackScreenView(screenIndex: number, totalScreens: number): void {
    if (!this.isEnabled() || !browser) return;

    this.totalScreens = totalScreens;
    const fbq = (window as any).fbq;
    if (typeof fbq !== 'function') return;

    // Screen 0 (welcome/landing) - ViewContent
    if (screenIndex === 0) {
      const eventName = this.config?.events?.landOnWelcome || 'ViewContent';
      fbq('track', eventName, {
        content_type: 'survey',
        content_name: this.surveyId || 'unknown'
      });
    }
  }

  /**
   * Track when user starts the survey (screen 0 → 1)
   */
  trackSurveyStart(surveyId: string, eventId?: string): void {
    if (!this.isEnabled() || !browser) return;

    const fbq = (window as any).fbq;
    if (typeof fbq !== 'function') return;

    const eventName = this.config?.events?.startFirstQuestion || 'SurveyStarted';
    const eventData: any = {
      content_type: 'survey',
      content_name: surveyId,
      ...this.config?.options?.customData
    };

    // Add event_id for deduplication if provided
    const trackOptions: any = {};
    if (eventId) {
      trackOptions.eventID = eventId;
    }

    // Check if it's a standard event or custom event
    if (eventName === 'Lead' || eventName === 'CompleteRegistration' || eventName === 'ViewContent') {
      fbq('track', eventName, eventData, trackOptions);
    } else {
      fbq('trackCustom', eventName, eventData, trackOptions);
    }
  }

  /**
   * Track when user reaches survey midpoint
   */
  trackSurveyMidpoint(surveyId: string, screenIndex: number): void {
    if (!this.isEnabled() || !browser) return;

    const fbq = (window as any).fbq;
    if (typeof fbq !== 'function') return;

    // Calculate midpoint
    const configuredMidpoint = this.config?.options?.midpointScreen;
    const midpoint = configuredMidpoint !== undefined && configuredMidpoint !== null
      ? configuredMidpoint
      : Math.floor(this.totalScreens / 2);

    // Only fire once when reaching midpoint
    if (screenIndex !== midpoint) return;

    const eventName = this.config?.events?.reachMidpoint || 'SurveyMidpoint';
    const eventData = {
      content_type: 'survey_progress',
      content_name: surveyId,
      value: 0.5,
      ...this.config?.options?.customData
    };

    // Custom event
    fbq('trackCustom', eventName, eventData);
  }

  /**
   * Track survey completion - main conversion event
   */
  trackSurveyComplete(
    surveyId: string,
    metadata?: Record<string, any>,
    eventId?: string
  ): void {
    if (!this.isEnabled() || !browser) return;

    const fbq = (window as any).fbq;
    if (typeof fbq !== 'function') return;

    const eventName = this.config?.events?.completeSurvey || 'Lead';
    const eventData: any = {
      content_type: 'survey',
      content_name: surveyId,
      value: 1.0,
      currency: 'USD',
      ...this.config?.options?.customData,
      ...metadata
    };

    // Add event_id for deduplication with CAPI
    const trackOptions: any = {};
    if (eventId) {
      trackOptions.eventID = eventId;
    }

    // Track as standard event (Lead is standard)
    fbq('track', eventName, eventData, trackOptions);
  }

  /**
   * Track custom event
   */
  trackCustomEvent(eventName: string, data?: Record<string, any>): void {
    if (!this.isEnabled() || !browser) return;

    const fbq = (window as any).fbq;
    if (typeof fbq !== 'function') return;

    fbq('trackCustom', eventName, {
      content_type: 'survey',
      content_name: this.surveyId,
      ...this.config?.options?.customData,
      ...data
    });
  }

  /**
   * Get Facebook cookies for advanced matching
   */
  getCookies(): { fbc: string | null; fbp: string | null } {
    if (!browser) {
      return { fbc: null, fbp: null };
    }

    return {
      fbc: this.getCookie('_fbc'),
      fbp: this.getCookie('_fbp')
    };
  }

  /**
   * Helper to get cookie value
   */
  private getCookie(name: string): string | null {
    if (!browser) return null;

    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }
}

// Export singleton instance
export const metaPixelService = new MetaPixelService();

// Make service available globally for debugging
if (browser) {
  (window as any).__metaPixelService = metaPixelService;
}
